import { useEffect, useState } from 'react';
import { Panel } from 'react-resizable-panels';

import { soundAnalysisLine, soundRequest, type SoundAction } from '../../lib/ai-context.js';
import { askAssistant } from '../../lib/ask-ai.js';
import { soundPlayer, usePlayback } from '../../lib/sound-player.js';
import { renderPreviewDoc } from '../../lib/sound-render.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { selectionLabel, useSound, type SoundSide } from '../../state/sound.js';
import { useWorkspace } from '../../state/workspace.js';
import { PanelHeader } from '../scene/SceneTab.js';
import { isTyping } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { Handle, SplitGroup } from '../ui/layout.js';
import type { TimeView } from './analysis.js';
import { AnalysisPanel } from './AnalysisPanel.js';
import { DownloadsPanel } from './DownloadsPanel.js';
import { PianoRoll } from './PianoRoll.js';
import { PresetBrowser } from './PresetBrowser.js';
import { setPreviewRenderer } from './sound-api.js';
import { KIND_LABEL, newSound, SoundHeader } from './SoundHeader.js';
import { SoundGenPanel } from './SoundGenPanel.js';
import { SoundInspector } from './SoundInspector.js';
import { SoundOutline } from './SoundOutline.js';
import { SpectrogramView } from './SpectrogramView.js';
import { StepSequencer } from './StepSequencer.js';
import { Timeline } from './Timeline.js';
import { Transport } from './Transport.js';
import { WaveformView } from './WaveformView.js';
import './sound.css';
import { WEBSITE } from '../../lib/edition.js';

/**
 * The Sound tab: an AI-first editor for `*.sound.json` (sound effects,
 * instruments, songs, voices). Header (file, kind, presets, render), outline
 * on the left, timeline / piano roll / drum steps in the centre with the
 * rendered waveform + spectrogram below, inspector / analysis / presets /
 * downloads on the right, and the transport with the master meter at the
 * bottom. The AI prompt bar sends requests with the sound's context.
 */

const QUICK_ACTIONS: Array<{ action: SoundAction; label: string; icon: IconName; needsDoc?: boolean }> = [
  { action: 'generate', label: 'Generate new sound', icon: 'sparkles' },
  { action: 'refine', label: 'Refine', icon: 'wand', needsDoc: true },
  { action: 'variations', label: 'Add variations', icon: 'copy', needsDoc: true },
  { action: 'fix', label: 'Fix mix problems', icon: 'warning', needsDoc: true },
];

function SoundAiBar() {
  const path = useSound((s) => s.path);
  const doc = useSound((s) => s.doc);
  const selection = useSound((s) => s.selection);
  const analysis = useSound((s) => s.analysis);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const label = selectionLabel(doc, selection);

  async function send(action: SoundAction) {
    if (busy || (action === 'custom' && !text.trim())) return;
    setBusy(true);
    await useSound.getState().flush();
    const ok = await askAssistant(
      soundRequest({
        action,
        prompt: text,
        soundPath: path,
        selection: label,
        analysis: analysis ? soundAnalysisLine(analysis) : null,
      }),
    );
    setBusy(false);
    if (ok) setText('');
  }

  return (
    <div className="ai-prompt snd-ai">
      <form
        className="ai-prompt-row"
        onSubmit={(e) => {
          e.preventDefault();
          void send(path ? 'custom' : 'generate');
        }}
      >
        <Icon name="sparkles" size={16} className="ai-glyph" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={path ? 'Describe what to change (e.g. "punchier attack, less low end")…' : 'Describe a sound, instrument, song or voice line to create…'}
          aria-label="Describe the sound to create or change"
        />
        {label && (
          <span className="ctx-chip ctx-part" title={`Selected: ${label}`}>
            <Icon name="layers" size={11} /> {label}
          </span>
        )}
        <button type="submit" className="primary small" disabled={busy || !text.trim()} aria-label="Send to Prisma">
          <Icon name="send" size={13} />
        </button>
      </form>
      <div className="ai-actions">
        {QUICK_ACTIONS.map((q) => (
          <button
            key={q.action}
            type="button"
            className="ai-action"
            disabled={busy || (q.needsDoc && !path)}
            onClick={() => void send(q.action)}
            title={q.label}
          >
            <Icon name={q.icon} size={12} /> {q.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CenterView() {
  const view = useSound((s) => s.view);
  if (view === 'pianoRoll') return <PianoRoll />;
  if (view === 'steps') return <StepSequencer />;
  return <Timeline />;
}

function RenderedViews() {
  const audio = useSound((s) => s.audio);
  const analysis = useSound((s) => s.analysis);
  const render = useSound((s) => s.render);
  const playhead = usePlayback((s) => s.position);
  const [view, setView] = useState<TimeView | undefined>(undefined);
  const [mode, setMode] = useState<'both' | 'wave' | 'spec'>('both');
  useEffect(() => setView(undefined), [audio]);
  if (!audio) {
    return (
      <div className="snd-rendered pane-empty muted">
        {render.status === 'rendering' ? `${render.stage || 'Rendering'}…` : render.status === 'error' ? `Render failed: ${render.error}` : 'Not rendered yet — the waveform and spectrogram appear here after a render.'}
      </div>
    );
  }
  const common = {
    left: audio.left,
    right: audio.right,
    rate: audio.rate,
    view,
    onViewChange: setView,
    playhead,
    onSeek: (t: number) => soundPlayer.seek(t),
    analysis,
  };
  return (
    <div className="snd-rendered">
      <div className="snd-rendered-tools">
        <div className="tb-group segmented" role="tablist" aria-label="Rendered views">
          {(['both', 'wave', 'spec'] as const).map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} className={`tb-btn${mode === m ? ' is-active' : ''}`} onClick={() => setMode(m)}>
              {m === 'both' ? 'Both' : m === 'wave' ? 'Waveform' : 'Spectrogram'}
            </button>
          ))}
        </div>
        {render.status === 'error' && (
          <span className="snd-render-error" title={render.error ?? ''}>
            <Icon name="warning" size={12} /> Last render failed — showing the previous one
          </span>
        )}
      </div>
      <div className="snd-rendered-body">
        {mode !== 'spec' && <WaveformView {...common} height={mode === 'both' ? 90 : 180} />}
        {mode !== 'wave' && <SpectrogramView {...common} height={mode === 'both' ? 150 : 240} />}
      </div>
    </div>
  );
}

const SIDE_TABS: Array<{ id: SoundSide; label: string }> = [
  { id: 'inspector', label: 'Inspector' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'presets', label: 'Presets' },
  { id: 'downloads', label: 'Downloads' },
];

function SidePanel() {
  const side = useSound((s) => s.side);
  const doc = useSound((s) => s.doc);
  const audio = useSound((s) => s.audio);
  const analysis = useSound((s) => s.analysis);
  const warnings = useSound((s) => s.render.warnings);
  const s = useSound.getState();
  return (
    <>
      <div className="subtabs panel-subtabs" role="tablist">
        {SIDE_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={side === t.id} className={`subtab${side === t.id ? ' is-active' : ''}`} onClick={() => s.setSide(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="inspector snd-side">
        {side === 'inspector' && <SoundInspector />}
        {side === 'analysis' &&
          (audio || analysis ? (
            <AnalysisPanel analysis={analysis} left={audio?.left} right={audio?.right} rate={audio?.rate} warnings={warnings} doc={doc} onSeek={(t) => soundPlayer.seek(t)} />
          ) : (
            <div className="pane-empty muted">Render the sound to see its analysis.</div>
          ))}
        {side === 'presets' && <PresetBrowser doc={doc} onCreated={(path) => void useSound.getState().open(path)} />}
        {side === 'downloads' && <DownloadsPanel />}
      </div>
    </>
  );
}

function EmptyState() {
  const entries = useProjectFiles((s) => s.entries);
  const error = useSound((s) => s.error);
  const loading = useSound((s) => s.loading);
  const sounds = filesOfKind(entries, 'sound');
  return (
    <div className="snd-empty">
      <Icon name="audio" size={24} />
      <strong>{loading ? 'Opening…' : error ? 'Could not open the sound' : 'Open or create a sound'}</strong>
      {error && <span className="mono muted">{error}</span>}
      {sounds.length > 0 && (
        <div className="scene-list">
          {sounds.slice(0, 10).map((p) => (
            <button key={p} type="button" className="ghost small" onClick={() => void useSound.getState().open(p)}>
              {p}
            </button>
          ))}
        </div>
      )}
      <div className="scene-list">
        {(['sfx', 'instrument', 'song', 'voice'] as const).map((k) => (
          <button key={k} type="button" className={k === 'sfx' ? 'primary small' : 'ghost small'} onClick={() => void newSound(k)}>
            <Icon name="plus" size={13} /> {KIND_LABEL[k]}
          </button>
        ))}
        <button type="button" className="ghost small" onClick={() => useSound.getState().setSide('presets')}>
          <Icon name="sparkles" size={13} /> From a preset
        </button>
      </div>
      <span className="muted">…or describe one to the AI below.</span>
    </div>
  );
}

export function SoundTab({ active }: { active: boolean }) {
  const doc = useSound((s) => s.doc);
  const path = useSound((s) => s.path);

  // Editors' preview buttons render through the tab's worker (with the project's files).
  useEffect(() => {
    setPreviewRenderer((d, p) => {
      const pid = useWorkspace.getState().projectId;
      if (!pid) return Promise.reject(new Error('no project open'));
      return renderPreviewDoc(pid, d, p);
    });
    return () => setPreviewRenderer(null);
  }, []);

  // Stop playback when leaving the tab.
  useEffect(() => {
    if (!active && usePlayback.getState().playing) soundPlayer.stop();
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const s = useSound.getState();
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const inPatternView = s.view === 'pianoRoll' || s.view === 'steps';
      if (ctrl && key === 'z' && !e.shiftKey) s.undo();
      else if (ctrl && (key === 'y' || (key === 'z' && e.shiftKey))) s.redo();
      else if (ctrl && key === 'd') s.duplicateSelection();
      else if (ctrl && key === 's') void s.flush();
      else if (ctrl && e.key === 'Enter') void s.renderNow();
      else if (ctrl && key === 'a' && s.view === 'pianoRoll' && s.activePattern) {
        s.selectNotes((s.doc?.patterns?.[s.activePattern]?.notes ?? []).map((_, i) => i));
      } else if (ctrl || e.altKey) return;
      else if (e.key === ' ' || e.code === 'Space') {
        if ((e.target as HTMLElement | null)?.tagName === 'BUTTON') return;
        soundPlayer.toggle();
      } else if (e.key === 'Home') soundPlayer.seek(0);
      else if (key === 'l') {
        const pb = usePlayback.getState();
        if (!pb.loop && s.audio) soundPlayer.setLoop({ start: 0, end: s.audio.left.length / s.audio.rate });
        else soundPlayer.setLoopOn(!pb.loopOn);
      } else if (e.key === 'Delete' || e.key === 'Backspace') s.deleteSelection();
      else if (e.key === 'ArrowLeft') s.nudge(-1, 0, e.shiftKey);
      else if (e.key === 'ArrowRight') s.nudge(1, 0, e.shiftKey);
      else if (e.key === 'ArrowUp') s.nudge(0, inPatternView ? (e.shiftKey ? 12 : 1) : -1);
      else if (e.key === 'ArrowDown') s.nudge(0, inPatternView ? (e.shiftKey ? -12 : -1) : 1);
      else if (e.key === 'Escape') {
        if (s.notes.length) s.selectNotes([]);
        else s.select(null);
      } else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="tab-body tab-sound">
      <SplitGroup id="sound-h" orientation="horizontal" fallback={{ outline: 16, center: 60, side: 24 }}>
        <Panel id="outline" minSize={180} className="pane">
          <PanelHeader title="Outline" />
          {doc ? <SoundOutline /> : <div className="pane-empty muted">No sound open</div>}
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="center" minSize={360} className="pane pane-center snd-center">
          <SoundHeader />
          {!WEBSITE && <SoundGenPanel />}
          {doc ? (
            <SplitGroup id="sound-v" orientation="vertical" fallback={{ editor: 66, rendered: 34 }}>
              <Panel id="editor" minSize={160} className="snd-editor-pane">
                <CenterView />
                <SoundAiBar />
              </Panel>
              <Handle orientation="horizontal" />
              <Panel id="rendered" minSize={90} className="snd-rendered-pane">
                <RenderedViews />
              </Panel>
            </SplitGroup>
          ) : (
            <div className="snd-editor-pane">
              <EmptyState />
              <SoundAiBar />
            </div>
          )}
          {path && <Transport />}
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="side" minSize={260} className="pane">
          <SidePanel />
        </Panel>
      </SplitGroup>
    </div>
  );
}
